// web-commands.ts — the PER-APP half of the web shape: maps oriora_send_log's Tauri data commands
// (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore. Plain CRUD
// is a few lines each. The derived command (compute_signals) is computed on-device in Rust
// (signals.rs) — 2026-08-19: now mirrored 1:1 in ./signals-web.ts (the zero-stub era meant
// "recap my season" claimed there was nothing to recap while climbs sat on screen).

import type { CommandHandler, WebStore } from "./web-runtime";
import { gradeValue } from "./grade-web";
import { computeSignalsWeb, type WebClimb } from "./signals-web";
import { browserSearch, browserFetch } from "./web-search-browser";

const TABLE = "climbs";
// Mirrors signals.rs::SEASON_DAYS — the project board only counts this season's work.
const SEASON_DAYS = 365;

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    list_climbs: ({ from, to, limit }) => {
      let rows = db.all(TABLE);
      if (from) rows = rows.filter((r) => String(r.climbed_on) >= String(from));
      if (to) rows = rows.filter((r) => String(r.climbed_on) <= String(to));
      rows.sort((a, b) => String(b.climbed_on).localeCompare(String(a.climbed_on)));
      return typeof limit === "number" ? rows.slice(0, limit) : rows;
    },
    get_climb: ({ id }) => db.get(TABLE, String(id)),
    create_climb: ({ input }) => {
      const now = nowIso();
      const base = {
        id: uuid(),
        climbed_on: todayLocal(),
        grade: "",
        grade_value: -1, // TODO: port derived logic to JS (grade.rs); -1 = unparseable on web
        environment: "indoor",
        venue: "",
        discipline: "boulder",
        style: [] as string[],
        ascent: "attempt",
        attempts: 1,
        sent: false,
        project: false,
        shoe: "",
        partner: "",
        note: "",
        photo: "",
        created_at: now,
        updated_at: now,
      };
      // Strip explicitly-undefined keys (they would clobber the defaults above — the Stash
      // file-a-box crash pattern), then compute the grade ordinal the Rust side computes.
      const given = Object.fromEntries(Object.entries(input as Record<string, unknown>).filter(([, v]) => v !== undefined));
      const row = { ...base, ...given };
      row.grade_value = gradeValue(String(row.grade ?? ""));
      return db.insert(TABLE, row as { id: string } & Record<string, unknown>);
    },
    update_climb: ({ id, patch }) => {
      const p: Record<string, unknown> = { ...(patch as Record<string, unknown>), updated_at: nowIso() };
      if (typeof p.grade === "string") p.grade_value = gradeValue(p.grade);
      db.update(TABLE, String(id), p);
      return null;
    },
    // Attach a photo to a climb — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_climb_photo: ({ id, photo }) => {
      db.update(TABLE, String(id), { photo: String(photo ?? ""), updated_at: nowIso() });
      return null;
    },
    delete_climb: ({ id }) => {
      db.remove(TABLE, String(id));
      return null;
    },
    // Derived aggregate — native computes rich SeasonSignals in signals.rs. Web shell = valid-but-
    // empty so the recap UI never crashes; degraded until the signal logic is ported to JS.
    // TODO: port derived logic to JS.
    compute_signals: ({ today }) =>
      computeSignalsWeb(db.all(TABLE) as unknown as WebClimb[], String(today ?? todayLocal())),
  };
}
